// Draws the play-field: ground, cities, bases, missiles + trails, explosions,
// and the crosshair. Operates in field coordinates (transform set by renderer).

import type { GameState, Vec2 } from '../core';
import { FIELD_W, FIELD_H, GROUND_Y } from '../core';
import { PALETTE, explosionColor, type WavePalette } from './palette';
import { drawFlying } from './drawFlying';

type Ctx = CanvasRenderingContext2D;

function drawBackground(ctx: Ctx, pal: WavePalette): void {
  ctx.fillStyle = pal.sky;
  ctx.fillRect(0, 0, FIELD_W, FIELD_H);
  ctx.fillStyle = pal.ground;
  ctx.fillRect(0, GROUND_Y, FIELD_W, FIELD_H - GROUND_Y);
  ctx.strokeStyle = pal.groundLine;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, GROUND_Y);
  ctx.lineTo(FIELD_W, GROUND_Y);
  ctx.stroke();
}

function drawCities(ctx: Ctx, state: GameState): void {
  for (const c of state.cities) {
    const { x, y } = c.pos;
    if (c.alive) {
      ctx.fillStyle = PALETTE.city;
      ctx.fillRect(x - 22, y - 26, 44, 26);
      ctx.fillStyle = PALETTE.sky;
      ctx.fillRect(x - 14, y - 20, 6, 14);
      ctx.fillRect(x + 8, y - 20, 6, 14);
    } else {
      ctx.fillStyle = PALETTE.cityDead;
      ctx.beginPath();
      ctx.moveTo(x - 22, y);
      ctx.lineTo(x - 8, y - 8);
      ctx.lineTo(x + 6, y - 4);
      ctx.lineTo(x + 22, y - 10);
      ctx.lineTo(x + 22, y);
      ctx.closePath();
      ctx.fill();
    }
  }
}

function drawBases(ctx: Ctx, state: GameState): void {
  for (const b of state.bases) {
    const { x, y } = b.pos;
    ctx.fillStyle = b.alive ? PALETTE.base : PALETTE.baseDead;
    ctx.beginPath();
    ctx.moveTo(x - 30, y);
    ctx.lineTo(x, y - 30);
    ctx.lineTo(x + 30, y);
    ctx.closePath();
    ctx.fill();
    // ammo stack
    ctx.fillStyle = PALETTE.ammo;
    for (let i = 0; i < b.ammo; i++) {
      const col = i % 4;
      const row = Math.floor(i / 4);
      ctx.fillRect(x - 12 + col * 7, y - 8 - row * 5, 4, 4);
    }
  }
}

function drawTrail(ctx: Ctx, from: Vec2, to: Vec2, color: string): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
}

function drawMissiles(ctx: Ctx, state: GameState, pal: WavePalette): void {
  for (const e of state.enemyMissiles) {
    drawTrail(ctx, e.origin, e.pos, pal.enemyTrail);
    ctx.fillStyle = e.kind === 'smartBomb' ? '#ff3df0' : pal.enemy;
    if (e.kind === 'smartBomb') {
      ctx.fillRect(e.pos.x - 3, e.pos.y - 3, 6, 6);
    } else {
      ctx.fillRect(e.pos.x - 2, e.pos.y - 2, 4, 4);
    }
  }
  for (const m of state.playerMissiles) {
    drawTrail(ctx, m.origin, m.pos, PALETTE.playerTrail);
    ctx.fillStyle = PALETTE.player;
    ctx.fillRect(m.pos.x - 2, m.pos.y - 2, 4, 4);
    // target marker
    ctx.strokeStyle = PALETTE.target;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(m.target.x - 4, m.target.y - 4);
    ctx.lineTo(m.target.x + 4, m.target.y + 4);
    ctx.moveTo(m.target.x + 4, m.target.y - 4);
    ctx.lineTo(m.target.x - 4, m.target.y + 4);
    ctx.stroke();
  }
}

function drawExplosions(ctx: Ctx, state: GameState): void {
  for (const ex of state.explosions) {
    if (ex.radius <= 0) continue;
    ctx.fillStyle = explosionColor(ex.age, ex.phase);
    ctx.beginPath();
    ctx.arc(ex.pos.x, ex.pos.y, ex.radius, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawCrosshair(ctx: Ctx, p: Vec2): void {
  ctx.strokeStyle = PALETTE.crosshair;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(p.x - 10, p.y);
  ctx.lineTo(p.x + 10, p.y);
  ctx.moveTo(p.x, p.y - 10);
  ctx.lineTo(p.x, p.y + 10);
  ctx.stroke();
}

export function drawWorld(
  ctx: Ctx,
  state: GameState,
  pointer: Vec2,
  pal: WavePalette,
): void {
  drawBackground(ctx, pal);
  drawCities(ctx, state);
  drawBases(ctx, state);
  drawFlying(ctx, state, pal);
  drawMissiles(ctx, state, pal);
  drawExplosions(ctx, state);
  if (state.phase.kind === 'playing') drawCrosshair(ctx, pointer);
}
